import type {
    AiqmTrainee,
    AiqmUploader,
    CallSession,
    CallSessionListItem,
    CallSessionStatus,
    TranscriptEntry,
} from "@type/aiqm";
import type { RubricCriterion } from "@type/training";
import axios from "axios";
import { getCookie } from "@util/cookie";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
}

export interface CallSessionListParams {
    page?: number;
    per_page?: number;
    status?: string;
    campaign_id?: string;
    search?: string;
    // Filter to calls uploaded by this user id. BACKEND: not yet supported —
    // see BE_HANDOFF_AIQM_CALL_TAGS.md.
    uploaded_by?: string;
    // Filter to calls handled by this trainee id (single). AND-combined with the
    // other filters. Source: BE #3193.
    handled_by?: string;
    // Filter to calls carrying ANY of these tags (OR-within-tags, AND with the
    // other filters). Sent as a repeated `?tags=` param. Source: BE #3193.
    tags?: string[];
}

export interface CallSessionListResponse {
    sessions: CallSessionListItem[];
    total: number;
    page: number;
    per_page: number;
}

export async function getCallSessions(
    params: CallSessionListParams = {}
): Promise<CallSessionListResponse> {
    const query = new URLSearchParams();
    if (params.page) query.set("page", String(params.page));
    if (params.per_page) query.set("per_page", String(params.per_page));
    if (params.status) query.set("status", params.status);
    if (params.campaign_id) query.set("campaign_id", params.campaign_id);
    if (params.search) query.set("search", params.search);
    if (params.uploaded_by) query.set("uploaded_by", params.uploaded_by);
    if (params.handled_by) query.set("handled_by", params.handled_by);
    // OR-within-tags: repeat the param per tag (?tags=a&tags=b) so the BE reads
    // it as a list. Mirrors the Insights sentiment filter's tag encoding.
    if (params.tags && params.tags.length > 0) {
        for (const t of params.tags) query.append("tags", t);
    }

    const qs = query.toString();
    const url = `${apiURL}/training/aiqm/sessions${qs ? `?${qs}` : ""}`;
    const res = await axios.get(url, { headers: authHeaders() });

    // BE response: `{ code, data: CallSessionListItem[], meta: { pagination } }`.
    // We also defensively accept the legacy `{ data: { sessions, total, ... } }`
    // envelope so older deployments don't break the page.
    const body = res.data ?? {};
    const dataField = body.data;
    const sessions: CallSessionListItem[] = Array.isArray(dataField)
        ? dataField
        : Array.isArray(dataField?.sessions)
          ? dataField.sessions
          : [];
    const pagination = body.meta?.pagination ?? {};
    const total =
        typeof pagination.total === "number"
            ? pagination.total
            : typeof dataField?.total === "number"
              ? dataField.total
              : sessions.length;
    const page =
        typeof pagination.page === "number"
            ? pagination.page
            : typeof dataField?.page === "number"
              ? dataField.page
              : (params.page ?? 1);
    const per_page =
        typeof pagination.per_page === "number"
            ? pagination.per_page
            : typeof dataField?.per_page === "number"
              ? dataField.per_page
              : (params.per_page ?? 10);

    return { sessions, total, page, per_page };
}

// Chat/TXT AIQM uploads carry `time_in_chat` on each transcript entry instead of
// the audio `time_in_call_secs`/`timestamp_secs`. It is a wall-clock datetime
// string like "17/03/2026 12:30:55" (DD/MM/YYYY HH:MM:SS). Pull out the HH:MM to
// show as the transcript time label, plus a `secs` value for ordering.
// Returns {} when absent/unparseable.
function chatTimeFromString(v: unknown): { label?: string; secs?: number } {
    if (typeof v !== "string") return {};
    // Full "DD/MM/YYYY HH:MM:SS" datetime → epoch seconds, so ordering stays
    // monotonic even when a chat crosses midnight (seconds-since-midnight would
    // make 00:05 sort before 23:58).
    const full = v.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (full) {
        const [, dd, mo, yyyy, hh, mm, ss = "0"] = full;
        const date = new Date(
            Number(yyyy),
            Number(mo) - 1,
            Number(dd),
            Number(hh),
            Number(mm),
            Number(ss),
        );
        if (!Number.isNaN(date.getTime())) {
            return {
                label: `${Number(hh)}:${String(Number(mm)).padStart(2, "0")}`,
                secs: Math.floor(date.getTime() / 1000),
            };
        }
    }
    // Clock-only string ("HH:MM[:SS]") → seconds-since-midnight fallback.
    const clock = v.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!clock) return {};
    const hh = Number(clock[1]);
    const mm = Number(clock[2]);
    const ss = clock[3] !== undefined ? Number(clock[3]) : 0;
    if (hh > 23 || mm > 59 || ss > 59) return {};
    return {
        label: `${hh}:${String(mm).padStart(2, "0")}`,
        secs: hh * 3600 + mm * 60 + ss,
    };
}

// BE transcript shape uses ElevenLabs role naming + a different timestamp
// field, so the FE shape (`speaker`, `timestamp_secs`, `order`) needs an
// explicit adapter.
//
// In a training AIQM roleplay the human trainee plays the CSR agent and the
// AI plays the customer being called. ElevenLabs labels them as:
//   - role "user"  = human trainee  → we render as "Agent"
//   - role "agent" = AI customer    → we render as "Contact"
// That's why the speaker fields are flipped below.
function adaptTranscriptEntry(raw: unknown, idx: number): TranscriptEntry {
    const r = (raw ?? {}) as Record<string, unknown>;
    const role = typeof r.role === "string" ? r.role : undefined;
    const existingSpeaker =
        r.speaker === "agent" || r.speaker === "user" ? r.speaker : undefined;
    const speaker: TranscriptEntry["speaker"] =
        existingSpeaker ??
        (role === "user" ? "agent" : role === "agent" ? "user" : "agent");
    // Chat/TXT uploads use `time_in_chat` (a wall-clock datetime); audio calls use
    // timestamp_secs / time_in_call_secs. `time_in_chat` is only present on TXT
    // sessions, so preferring it here is safe for audio (it's absent there). For
    // chat we show the HH:MM label (`ts_label`) and keep seconds-since-midnight in
    // timestamp_secs so ordering/timeline features stay monotonic.
    const chat = chatTimeFromString(r.time_in_chat);
    const seconds =
        chat.secs ??
        (typeof r.timestamp_secs === "number"
            ? r.timestamp_secs
            : typeof r.time_in_call_secs === "number"
              ? r.time_in_call_secs
              : 0);
    return {
        speaker,
        content: typeof r.content === "string" ? r.content : "",
        timestamp_secs: Number.isFinite(seconds) ? seconds : 0,
        order: typeof r.order === "number" ? r.order : idx,
        ts_label: chat.label,
    };
}

export async function getCallSession(sessionId: string): Promise<CallSession> {
    const res = await axios.get(`${apiURL}/training/aiqm/sessions/${sessionId}`, {
        headers: authHeaders(),
    });
    const raw = (res.data?.data ?? {}) as Record<string, unknown>;
    const rawTranscript = Array.isArray(raw.transcript) ? raw.transcript : [];
    return {
        ...(raw as unknown as CallSession),
        transcript: rawTranscript.map(adaptTranscriptEntry),
    };
}

export interface UploadAudioResponse {
    session_id: string;
    filename: string;
    status: CallSessionStatus;
}

// `POST /training/aiqm/upload` now also returns a `batch_id` (BE #3090) used to
// correlate the per-file progress WebSocket events for this upload batch.
export interface UploadAudioResult {
    sessions: UploadAudioResponse[];
    batchId: string | null;
}


export interface UploadAudioOptions {
    campaignId?: string;
    // Rubric snapshot to score this upload against. Sent as a JSON string in
    // the multipart payload; once BE wires up rubric scoring it will snapshot
    // this onto each created TrainingAiqmSession (same rubric applies to all
    // files in a single upload request). Today BE silently ignores it.
    rubricCriteria?: RubricCriterion[];
    // Free-text tags applied to every call in this upload batch, used to group
    // calls for the Insights tag filter. Sent as a JSON string in the multipart
    // payload. BACKEND: not yet persisted — see BE_HANDOFF_AIQM_CALL_TAGS.md.
    tags?: string[];
    // Upload-scoped knowledge (Trainer KB Phase 2 — see FE repo
    // docs/TRAINING_KB_SCOPES_BE_HANDOFF_2026-07-16.md). PDF documents that
    // influence ONLY this batch's evaluation, sent as repeated
    // `knowledge_files` multipart entries. BACKEND: live — the scoped-upload
    // contract (Phases 1+2) shipped in ourteam-backend#3588 (merged to main
    // 2026-07-17); the upload endpoint consumes these fields in prod.
    knowledgeFiles?: File[];
    // Workspace KB docs the user removed for this batch (they apply by
    // default). Sent as a JSON string `excluded_knowledge_document_ids`.
    excludedKnowledgeDocumentIds?: string[];
}

// Pull the human-facing rejection reason out of the upload endpoint's error
// body so the toast can show WHY it failed. The BE shape is
// `{ code, message, errors: string[], data: { rejected: [{ filename, error }] } }`.
// Prefer the top-level `message`, then the first `errors[]` detail, then a
// per-file `rejected[].error`. Returns null when there's nothing usable.
function extractUploadErrorMessage(data: unknown): string | null {
    if (!data || typeof data !== "object") return null;
    const d = data as {
        message?: unknown;
        errors?: unknown;
        data?: { rejected?: Array<{ error?: unknown }> };
    };
    if (typeof d.message === "string" && d.message.trim()) return d.message.trim();
    if (Array.isArray(d.errors) && typeof d.errors[0] === "string" && d.errors[0].trim()) {
        return d.errors[0].trim();
    }
    const rejected = d.data?.rejected?.[0]?.error;
    if (typeof rejected === "string" && rejected.trim()) return rejected.trim();
    return null;
}

// Upload up to 10 audio files in a single multipart request.
// BE contract (FE_HANDOFF_AIQM_MULTI_UPLOAD_AND_AUDIO.md):
//   - Field name is `files` (repeated for each file)
//   - campaign_id goes as a query param, NOT in the body
//   - Validation is all-or-nothing: if any file is invalid, the entire
//     request is rejected with 400 and no sessions are created
//   - Response data is always an array, even for a single file
export async function uploadAudioFiles(
    files: File[],
    options: UploadAudioOptions = {}
): Promise<UploadAudioResult> {
    const { campaignId, rubricCriteria, tags, knowledgeFiles, excludedKnowledgeDocumentIds } =
        options;
    const formData = new FormData();
    for (const file of files) {
        formData.append("files", file);
    }
    if (rubricCriteria && rubricCriteria.length > 0) {
        formData.append("rubric_criteria", JSON.stringify(rubricCriteria));
    }
    if (knowledgeFiles && knowledgeFiles.length > 0) {
        for (const kf of knowledgeFiles) {
            formData.append("knowledge_files", kf);
        }
    }
    if (excludedKnowledgeDocumentIds && excludedKnowledgeDocumentIds.length > 0) {
        formData.append(
            "excluded_knowledge_document_ids",
            JSON.stringify(excludedKnowledgeDocumentIds),
        );
    }
    if (tags && tags.length > 0) {
        formData.append("tags", JSON.stringify(tags));
    }

    const query = new URLSearchParams();
    if (campaignId) {
        query.set("campaign_id", campaignId);
    }
    const qs = query.toString();
    const url = `${apiURL}/training/aiqm/upload${qs ? `?${qs}` : ""}`;

    const token = getCookie("authToken");
    let res;
    try {
        res = await axios.post(url, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            timeout: 300_000,
        });
    } catch (err) {
        // Surface the backend's own rejection message (e.g. "Upload limit reached
        // for your plan") instead of the opaque axios "status code 4xx" string.
        // Network/timeout failures with no response body keep the original error
        // so the toast's connectivity mapping still applies.
        const beMessage = extractUploadErrorMessage(
            (err as { response?: { data?: unknown } })?.response?.data,
        );
        if (beMessage) throw new Error(beMessage);
        throw err;
    }
    const body = res.data?.data ?? {};
    const sessions: UploadAudioResponse[] = Array.isArray(body)
        ? body
        : (body.created ?? []);
    const batchId: string | null =
        !Array.isArray(body) && typeof body.batch_id === "string"
            ? body.batch_id
            : null;
    return { sessions, batchId };
}

// Fetch a presigned S3 URL to play back the original uploaded audio.
// URL expires after 1 hour — never cache across page navigations.
export interface AiqmAudioResponse {
    audio_url: string;
    filename: string;
}

export async function getAiqmAudio(sessionId: string): Promise<AiqmAudioResponse> {
    const res = await axios.get(
        `${apiURL}/training/aiqm/sessions/${sessionId}/audio`,
        { headers: authHeaders() },
    );
    return res.data?.data ?? { audio_url: "", filename: "" };
}

// Update the tags on a single existing call session — lets users tag/retag
// previously-uploaded calls directly from the Calls list, including past
// uploads that predate the tagging feature.
//
// Real endpoint (when built): PATCH /training/aiqm/sessions/:id/tags
//   body: { tags: string[] }  → returns the updated tag list
// BACKEND: not yet implemented — see BE_HANDOFF_AIQM_CALL_TAGS.md.
//
// In mock mode the call is a no-op that echoes the tags back so the UI is
// fully demoable.
export async function updateCallTags(
    sessionId: string,
    tags: string[],
): Promise<string[]> {
    const useMock = import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true";
    if (useMock) {
        return tags;
    }
    const res = await axios.patch(
        `${apiURL}/training/aiqm/sessions/${sessionId}/tags`,
        { tags },
        { headers: authHeaders() },
    );
    const data = res.data?.data ?? res.data;
    return Array.isArray(data?.tags)
        ? data.tags.filter((x: unknown): x is string => typeof x === "string")
        : tags;
}

// List the distinct people who have uploaded AIQM calls for this client, to
// populate the "Uploaded by" filter.
//
// Real endpoint (when built): GET /training/aiqm/uploaders
//   - auth-scoped; returns [{ id, name }]
// BACKEND: not yet implemented — see BE_HANDOFF_AIQM_CALL_TAGS.md.
const MOCK_UPLOADERS: AiqmUploader[] = [
    { id: "u-1", name: "Aisyah Rahman", count: 12 },
    { id: "u-2", name: "Budi Santoso", count: 42 },
    { id: "u-3", name: "Chen Wei", count: 0 },
    { id: "u-4", name: "Dewi Lestari", count: 7 },
];

export async function listAiqmUploaders(): Promise<AiqmUploader[]> {
    const useMock = import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true";
    if (useMock) {
        return [...MOCK_UPLOADERS];
    }
    // NOTE: errors propagate to the caller on purpose. A 401/403/404/500 must be
    // distinguishable from a legitimately empty uploader list, otherwise a broken
    // auth-scoped route silently looks like "no uploaders". The page catches this
    // and hides the filter rather than rendering an empty, misleading dropdown.
    const res = await axios.get(`${apiURL}/training/aiqm/uploaders`, {
        headers: authHeaders(),
    });
    const data = res.data?.data ?? res.data;
    return Array.isArray(data)
        ? data
              .filter(
                  (u: unknown): u is AiqmUploader =>
                      !!u &&
                      typeof (u as AiqmUploader).id === "string" &&
                      typeof (u as AiqmUploader).name === "string",
              )
              .map((u) => ({
                  id: u.id,
                  name: u.name,
                  // Only keep count when it's a valid non-negative integer, so a
                  // malformed/missing value falls back to name-only rendering.
                  ...(typeof u.count === "number" &&
                  Number.isInteger(u.count) &&
                  u.count >= 0
                      ? { count: u.count }
                      : {}),
              }))
        : [];
}

// Rename a call session's filename (display name) from the Calls list.
//
// Real endpoint (when built): PATCH /training/aiqm/sessions/:id
//   body: { filename: string }  → returns the updated filename
// BACKEND: not yet implemented — see BE_HANDOFF_AIQM_CALL_TAGS.md.
//
// Mock mode echoes the new name back so the UI is fully demoable.
export async function renameCallSession(
    sessionId: string,
    filename: string,
): Promise<string> {
    const useMock = import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true";
    if (useMock) {
        return filename;
    }
    const res = await axios.patch(
        `${apiURL}/training/aiqm/sessions/${sessionId}`,
        { filename },
        { headers: authHeaders() },
    );
    const data = res.data?.data ?? res.data;
    return typeof data?.filename === "string" ? data.filename : filename;
}

// ───────────────────────────────────────────────────────────────────────────
// Handled-by (trainee) — list roster + assign endpoints (BE #3193)
//
// "Handled by" = the trainee who took the call, INDEPENDENT from "Uploaded by"
// (who imported it). Per BE #3193 (resolved decision A), trainees are workspace
// members, so the roster endpoint mirrors GET /training/aiqm/uploaders.
// ───────────────────────────────────────────────────────────────────────────

const MOCK_TRAINEES: AiqmTrainee[] = [
    { id: "t-maya", name: "Maya Lim", count: 18 },
    { id: "t-arjun", name: "Arjun Patel", count: 9 },
    { id: "t-sofia", name: "Sofia Garcia", count: 0 },
    { id: "t-kenji", name: "Kenji Tanaka", count: 5 },
];

// List the trainees who can handle AIQM calls, to populate the "Handled by"
// filter and the inline assign menu.
//
// Real endpoint (BE #3193): GET /training/aiqm/trainees
//   - auth-scoped; returns [{ id, name, count? }]
export async function listAiqmTrainees(): Promise<AiqmTrainee[]> {
    const useMock = import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true";
    if (useMock) {
        return [...MOCK_TRAINEES];
    }
    // NOTE: errors propagate to the caller on purpose (same rationale as
    // listAiqmUploaders) — a broken auth-scoped route must be distinguishable
    // from a legitimately empty roster, so the page can hide the filter rather
    // than render a misleading empty dropdown.
    const res = await axios.get(`${apiURL}/training/aiqm/trainees`, {
        headers: authHeaders(),
    });
    const data = res.data?.data ?? res.data;
    return Array.isArray(data)
        ? data
              .filter(
                  (t: unknown): t is AiqmTrainee =>
                      !!t &&
                      typeof (t as AiqmTrainee).id === "string" &&
                      typeof (t as AiqmTrainee).name === "string",
              )
              .map((t) => ({
                  id: t.id,
                  name: t.name,
                  ...(typeof t.count === "number" &&
                  Number.isInteger(t.count) &&
                  t.count >= 0
                      ? { count: t.count }
                      : {}),
              }))
        : [];
}

// Normalize a raw `handled_by` value from a BE response into the FE shape.
function parseHandledBy(raw: unknown): AiqmTrainee | null {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    if (typeof r.id === "string" && typeof r.name === "string") {
        return { id: r.id, name: r.name };
    }
    return null;
}

// Assign / reassign / unassign the trainee who handled a single call.
//
// Real endpoint (deployed — verified via staging OpenAPI): PATCH /training/aiqm/sessions/{session_id}/handler
//   body: { handled_by: traineeId | null }  → returns the updated session
//   - 403 for read-only members (gate the UI so they never reach this).
// Pass `null` to unassign. Returns the resolved handled_by (or null).
export async function assignCallHandler(
    sessionId: string,
    traineeId: string | null,
): Promise<AiqmTrainee | null> {
    const useMock = import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true";
    if (useMock) {
        if (traineeId === null) return null;
        return MOCK_TRAINEES.find((t) => t.id === traineeId) ?? null;
    }
    const res = await axios.patch(
        `${apiURL}/training/aiqm/sessions/${sessionId}/handler`,
        { handled_by: traineeId },
        { headers: authHeaders() },
    );
    const data = res.data?.data ?? res.data;
    return parseHandledBy(data?.handled_by);
}

// Bulk-assign one trainee as the handler across many calls in a single request.
//
// Real endpoint (deployed — verified via staging OpenAPI): PATCH /training/aiqm/assign-handler
//   body: { session_ids: string[], handled_by: traineeId | null }
//   - 403 for read-only members (gate the UI so they never reach this).
// Pass `null` to bulk-unassign. Resolves to the assigned trainee (or null).
export async function bulkAssignCallHandler(
    callIds: string[],
    traineeId: string | null,
): Promise<AiqmTrainee | null> {
    const useMock = import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true";
    if (useMock) {
        if (traineeId === null) return null;
        return MOCK_TRAINEES.find((t) => t.id === traineeId) ?? null;
    }
    const res = await axios.patch(
        `${apiURL}/training/aiqm/assign-handler`,
        { session_ids: callIds, handled_by: traineeId },
        { headers: authHeaders() },
    );
    const data = res.data?.data ?? res.data;
    return parseHandledBy(data?.handled_by);
}

// Bulk-add tags across many calls in a single request (ADD/merge semantics — the
// given tags are unioned into each call's existing tags, nothing is removed).
//
// Real endpoint (when built): PATCH /training/aiqm/assign-tags
//   body: { session_ids: string[], tags: string[] }  → adds `tags` to each call
// BACKEND: not yet implemented — see BE_HANDOFF_AIQM_CALL_TAGS.md (bulk tags).
//   Gate behind AIQM_TAGS_ENABLED so this is hidden until the endpoint ships.
//
// In mock mode the call is a no-op that echoes the applied tags back so the UI
// is fully demoable. Returns the tags that were applied.
export async function bulkAssignTags(
    callIds: string[],
    tags: string[],
): Promise<string[]> {
    const useMock = import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true";
    if (useMock) {
        return tags;
    }
    const res = await axios.patch(
        `${apiURL}/training/aiqm/assign-tags`,
        { session_ids: callIds, tags },
        { headers: authHeaders() },
    );
    const data = res.data?.data ?? res.data;
    return Array.isArray(data?.tags)
        ? data.tags.filter((x: unknown): x is string => typeof x === "string")
        : tags;
}
